import {
  Button,
  Card,
  Col,
  Empty,
  Flex,
  Row,
  Segmented,
  Skeleton,
  Tabs,
  Typography
} from "antd"
import React, { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { useParams } from "react-router"
import { useSearchParams } from "react-router-dom"

import { generateDocumentTitle } from "../../../constants/document-title.ts"
import { useAppDispatch } from "../../../hooks/use-app-dispatch.ts"
import { useAppSelector } from "../../../hooks/use-app-selector.ts"
import { getEntityName } from "../../../utils/user/get-entity-name.ts"
import * as services from "../api/services.ts"
import { specializationDisplayNames } from "../constants/specialization.ts"
import { asArray } from "../helpers/list-values.ts"
import {
  COMPLIANCE_DOCUMENT_ORDER,
  TRACKED_COMPLIANCE_DOCUMENT_TYPES
} from "../constants/compliance.ts"
import { ComplianceDocumentUploadModal } from "../components/ComplianceDocumentUploadModal.tsx"
import { SubcontractorProjectsTable } from "./SubcontractorProjectsTable.tsx"
import { SubcontractorFormModal } from "../components/SubcontractorForm/SubcontractorFormModal.tsx"
import { type SubcontractorFormData } from "../components/SubcontractorForm/subcontractor-form-data.ts"
import { ComplianceDocumentsPanel } from "./components/ComplianceDocumentsPanel.tsx"
import { ProjectTypesPanel } from "./components/ProjectTypesPanel.tsx"
import { SubcontractorProfileHeader } from "./components/SubcontractorProfileHeader.tsx"
import { TaxPaymentSections } from "./components/TaxPaymentSections.tsx"
import { useSubcontractorSectionPermissions } from "../hooks/use-section-permissions.ts"
import {
  fetchSubcontractorsFilterOptions,
  updateSubcontractor
} from "../redux/subcontractors.thunks.ts"
import {
  type SubcontractorComplianceDocumentType,
  type SubcontractorComplianceState,
  type SubcontractorListItem
} from "../types/types.ts"

const { Text } = Typography

type ProjectsMapView = "active" | "past"

const SubcontractorDetailPage = () => {
  const { id } = useParams<{ id: string }>()
  const [subcontractor, setSubcontractor] =
    useState<SubcontractorListItem | null>(null)
  const [fetching, setFetching] = useState(true)
  const { mayViewTaxOrPayment, mayEditCompliance, mayEditTax, mayEditPayment } =
    useSubcontractorSectionPermissions()
  // Only a 404 means the record is gone; a 500 or a dropped connection is a
  // failure to load, which the user can retry.
  const [loadError, setLoadError] = useState<"notFound" | "failed" | null>(null)
  const [reloadKey, setReloadKey] = useState(0)
  const { t } = useTranslation()
  // An expiry alert links to ?tab=documents&document=<type> so it lands on the
  // card whose Upload/Replace action resolves it. Read once as the initial
  // value rather than kept in sync: once the page is open the user's own tab
  // clicks win, and rewriting the URL under them would fight the Tabs.
  const [searchParams] = useSearchParams()
  const highlightedDocument = searchParams.get("document")
  const [activeTab, setActiveTab] = useState(
    () => searchParams.get("tab") || "overview"
  )
  const [projectsMapView, setProjectsMapView] =
    useState<ProjectsMapView>("active")
  const [editing, setEditing] = useState(false)
  const [uploadingType, setUploadingType] =
    useState<SubcontractorComplianceDocumentType | null>(null)
  const dispatch = useAppDispatch()
  const specializationOptions = useAppSelector(
    (state) => state.subcontractors.filterOptions?.specialization
  )
  const permissions = useAppSelector(
    (state) => state?.auth?.permissions?.codes?.subcontractors
  )
  const editAllowed = permissions?.subcontractors?.subcontractor_edit

  useEffect(() => {
    generateDocumentTitle("Subcontractor")
  }, [])

  const specializationOptionsLoaded = Boolean(specializationOptions?.length)
  useEffect(() => {
    if (!specializationOptionsLoaded)
      dispatch(fetchSubcontractorsFilterOptions())
  }, [dispatch, specializationOptionsLoaded])

  useEffect(() => {
    if (!id) return

    let cancelled = false
    // Never show the previous record while another one loads.
    setSubcontractor(null)
    setFetching(true)
    setLoadError(null)

    services.getSubcontractorDetails(id).then((response) => {
      if (cancelled) return

      if (!response.ok) {
        setLoadError(response.status === 404 ? "notFound" : "failed")
        setFetching(false)
        return
      }

      setSubcontractor(response.data as SubcontractorListItem)
      setFetching(false)
    })

    return () => {
      cancelled = true
    }
  }, [id, reloadKey])

  const handleSave = async ({
    send_invitation: _sendInvitation,
    ...data
  }: SubcontractorFormData) => {
    if (!id) return

    const result = await dispatch(updateSubcontractor({ ...data, id }))
    if (!updateSubcontractor.fulfilled.match(result)) return

    // The PATCH response omits the derived detail fields (compliance status,
    // counts, project types), so re-read the record rather than merging.
    const refreshed = await services.getSubcontractorDetails(id)
    if (refreshed.ok) setSubcontractor(refreshed.data as SubcontractorListItem)
    setEditing(false)
  }

  // One section's fields only. Answers false when the server refused, which
  // leaves that card in edit mode with what was typed still in it. The re-read
  // is the same one the profile form does, so both surfaces and the edit form
  // read one record rather than drifting copies.
  const handleSectionSave = async (data: Partial<SubcontractorFormData>) => {
    if (!id) return false

    const result = await dispatch(updateSubcontractor({ ...data, id }))
    if (!updateSubcontractor.fulfilled.match(result)) return false

    const refreshed = await services.getSubcontractorDetails(id)
    if (refreshed.ok) setSubcontractor(refreshed.data as SubcontractorListItem)
    return true
  }

  // A single new/replaced document changes several derived fields at once
  // (its own status, the sidebar badge, and — for License — cslb_expiration_date),
  // so a full re-read is simpler and safer than reconciling one card by hand.
  const handleDocumentUploaded = () => {
    setUploadingType(null)
    setReloadKey((n) => n + 1)
  }

  if (fetching && !subcontractor) {
    return (
      <Card size={"small"}>
        <Skeleton active paragraph={{ rows: 8 }} />
      </Card>
    )
  }

  if (loadError === "failed") {
    return (
      <Card size={"small"}>
        <Empty description={t("subcontractorDetail.loadError")}>
          <Button onClick={() => setReloadKey((n) => n + 1)}>
            {t("subcontractorDetail.retry")}
          </Button>
        </Empty>
      </Card>
    )
  }

  if (!subcontractor || loadError === "notFound") {
    return (
      <Card size={"small"}>
        <Empty description={t("subcontractorDetail.notFound")} />
      </Card>
    )
  }

  // A subcontractor's profile is editable by any GC holding subcontractor_edit
  // regardless of status — Active and Suspended included. There is no
  // subcontractor-facing edit surface to collide with (unlike a Customer's
  // own portal), so nothing is protected by locking it once they sign in.
  const isEditable = !!editAllowed
  const complianceStatus = subcontractor.compliance_status || {}
  // The tracked types always render as a card — missing or not — so an admin
  // sees exactly what still needs attention; everything else the
  // subcontractor supplied (W-9, Miscellaneous) rides along, data-driven as
  // before.
  const complianceDocumentTypes = Array.from(
    new Set([
      ...TRACKED_COMPLIANCE_DOCUMENT_TYPES,
      ...Object.keys(complianceStatus)
    ])
  ).sort(
    (a, b) =>
      COMPLIANCE_DOCUMENT_ORDER.indexOf(a) -
      COMPLIANCE_DOCUMENT_ORDER.indexOf(b)
  )
  const complianceEntries = complianceDocumentTypes.map((docType) => [
    docType,
    complianceStatus[docType as keyof typeof complianceStatus] as
      | { status: SubcontractorComplianceState; expiration_date: string | null }
      | undefined
  ]) as Array<
    [
      string,
      (
        | {
            status: SubcontractorComplianceState
            expiration_date: string | null
          }
        | undefined
      )
    ]
  >
  const documentByType = (docType: string) =>
    subcontractor.compliance_documents?.find((d) => d.document_type === docType)

  // "Business name" prefers the legal entity name a company subcontractor
  // supplied; falling back to the person's own name covers the common case of
  // an individual with no separate business identity. "Primary contact"
  // mirrors the same fallback so a solo subcontractor isn't shown as their own
  // contact twice — the name row only renders when there's a distinct contact.
  const businessName =
    subcontractor.legal_business_name || getEntityName(subcontractor)
  const contactName = subcontractor.primary_contact_name
  const contactEmail =
    subcontractor.primary_contact_email || subcontractor.email
  const contactPhone =
    subcontractor.primary_contact_phone || subcontractor.phone
  const trades = specializationDisplayNames(
    asArray(subcontractor.specialization),
    specializationOptions,
    subcontractor.specialization_other
  )
  const taxIdLabel = subcontractor.ein_last4
    ? "EIN"
    : subcontractor.ssn_last4
      ? "SSN"
      : "Tax ID"
  const taxIdValue = subcontractor.ein_last4
    ? `On file, ending ${subcontractor.ein_last4}`
    : subcontractor.ssn_last4
      ? `On file, ending ${subcontractor.ssn_last4}`
      : undefined

  const complianceDocuments = (
    <ComplianceDocumentsPanel
      entries={complianceEntries}
      documentByType={documentByType}
      isEditable={isEditable}
      mayEditCompliance={mayEditCompliance}
      highlightedDocument={highlightedDocument}
      onUpload={setUploadingType}
    />
  )

  return (
    <Flex vertical gap={16}>
      <SubcontractorProfileHeader
        subcontractor={subcontractor}
        businessName={businessName}
        contactName={contactName}
        contactEmail={contactEmail}
        contactPhone={contactPhone}
        trades={trades}
        isEditable={isEditable}
        onEdit={() => setEditing(true)}
      />

      {editing && (
        <SubcontractorFormModal
          onSave={handleSave}
          onCancel={() => setEditing(false)}
          // The API's nullable columns come back as null; the form models them as
          // absent, which antd treats identically.
          initialValues={subcontractor as Partial<SubcontractorFormData>}
        />
      )}

      {uploadingType && (
        <ComplianceDocumentUploadModal
          open
          subcontractorId={subcontractor.id}
          documentType={uploadingType}
          currentExpirationDate={documentByType(uploadingType)?.expiration_date}
          onClose={() => setUploadingType(null)}
          onUploaded={handleDocumentUploaded}
        />
      )}

      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        items={[
          {
            key: "overview",
            label: t("subcontractorDetail.tabs.overview"),
            children: (
              <Flex vertical gap={16}>
                <Row gutter={[16, 16]} align={"top"}>
                  <Col xs={24} lg={7}>
                    <ProjectTypesPanel
                      types={subcontractor.project_types ?? []}
                    />
                  </Col>
                  <Col xs={24} lg={17}>
                    {complianceDocuments}
                  </Col>
                </Row>
                <Card size={"small"} title={t("subcontractorDetail.projects")}>
                  <SubcontractorProjectsTable
                    subcontractorId={subcontractor.id}
                    preview
                    onViewAll={() => setActiveTab("projects")}
                  />
                </Card>
              </Flex>
            )
          },
          {
            key: "projects",
            label: t("subcontractorDetail.tabs.projects"),
            children: (
              <Flex vertical gap={16}>
                <Card size={"small"} title={t("subcontractorDetail.projects")}>
                  <SubcontractorProjectsTable
                    subcontractorId={subcontractor.id}
                  />
                </Card>
                <Card
                  size={"small"}
                  title={t("subcontractorDetail.projectsMap")}
                  extra={
                    <Segmented
                      size={"small"}
                      value={projectsMapView}
                      onChange={(value) =>
                        setProjectsMapView(value as ProjectsMapView)
                      }
                      options={[
                        {
                          label: t("subcontractorDetail.active"),
                          value: "active"
                        },
                        { label: t("subcontractorDetail.past"), value: "past" }
                      ]}
                    />
                  }
                >
                  <Flex
                    align={"center"}
                    justify={"center"}
                    className={"h-64 rounded bg-gray-50"}
                  >
                    <Text type={"secondary"}>
                      {projectsMapView === "active"
                        ? t("subcontractorDetail.activeMapSoon")
                        : t("subcontractorDetail.pastMapSoon")}
                    </Text>
                  </Flex>
                </Card>
              </Flex>
            )
          },
          {
            key: "documents",
            label: t("subcontractorDetail.tabs.documents"),
            children: (
              <Flex vertical gap={16}>
                {complianceDocuments}
              </Flex>
            )
          },
          // The server already withholds the tax and payment fields from the
          // payload without the view rights, so this tab would render a card
          // of placeholders. Hide it rather than show an empty section that
          // reads as "nothing on file".
          ...(!mayViewTaxOrPayment
            ? []
            : [
                {
                  key: "tax",
                  label: t("subcontractorDetail.tabs.tax"),
                  children: (
                    <TaxPaymentSections
                      subcontractor={subcontractor}
                      taxIdLabel={taxIdLabel}
                      taxIdValue={taxIdValue}
                      mayEditIdentity={isEditable && mayEditTax}
                      mayEditCslb={isEditable}
                      mayEditPayment={isEditable && mayEditPayment}
                      onSaveSection={handleSectionSave}
                    />
                  )
                }
              ])
        ]}
      />
    </Flex>
  )
}

export default SubcontractorDetailPage
